import S3, { PutObjectRequest } from 'aws-sdk/clients/s3';
import { Credentials } from 'aws-sdk';
import { NullableString } from '../types/common';
import mime from 'mime';

/**
 * Sanitizes a user-supplied filename before it is used as part of an S3 object key.
 *
 * Strips directory components and control characters and rejects traversal sequences, so a
 * filename cannot escape the folder prefix it was scoped to. Without this, a name such as
 * `../otherChallenge/template.yaml` would write outside the intended challenge prefix.
 *
 * Note this does not prevent overwriting an existing object with the same sanitized name;
 * that requires a server-side existence check or a generated key prefix.
 */
export const sanitizeFileName = (fileName: string): string => {
  // Take the final path segment only, handling both POSIX and Windows separators.
  const baseName = fileName.split(/[/\\]/).pop() || '';

  const sanitized = baseName
    // Drop control characters and characters that are awkward or unsafe in an S3 key/URL.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[<>:"|?*]/g, '')
    .trim()
    // Collapse leading dots so '..' and hidden-file trickery cannot survive.
    .replace(/^\.+/, '');

  if (!sanitized) {
    throw new Error('Invalid file name');
  }

  return sanitized;
};

export const uploadFile = async (
  credentials: Credentials,
  bucketName: string,
  file: File,
  folder: NullableString = null
) => {
  const safeName = sanitizeFileName(file.name);
  const Key = folder ? `${folder}/${safeName}` : safeName;

  const params = {
    Body: file,
    Bucket: bucketName,
    Key,
    // ContentType is derived from the (sanitized) filename, which is client-controlled.
    // nosniff/Content-Disposition at the bucket or CDN layer, plus server-side validation,
    // are what actually prevent a renamed script-bearing file from being rendered.
    ContentType: mime.getType(safeName),
  } as PutObjectRequest;

  await new S3({
    credentials,
    region: 'us-west-2', // TODO: put the region in the environment
  })
    .upload(params)
    .promise();

  return `https://${bucketName}.s3.amazonaws.com/${Key}`;
};
